import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import axios from "../../config/axios";
import Loading from "components/Loading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Button,
  Paper,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box
} from "@material-ui/core";
import { Add as AddIcon, Delete as DeleteIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(theme => ({
  container: {},
  table: {
    marginTop: theme.spacing(2)
  },
  tableCell: {
    padding: theme.spacing(1.5)
  },
  input: {
    width: "100%"
  },
  saveButton: {
    marginLeft: theme.spacing(1)
  },
  headerRow: {
    backgroundColor: theme.palette.grey[200]
  },
  addButton: {
    marginBottom: theme.spacing(2)
  },
  deleteButton: {
    color: theme.palette.error.main
  }
}));

const PackagingMaterials = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStates, setEditingStates] = useState({});
  const [savingStates, setSavingStates] = useState({});
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [deletingMaterials, setDeletingMaterials] = useState({});
  const [newMaterial, setNewMaterial] = useState({
    nameEn: "",
    namePt: "",
    descriptionEn: "",
    descriptionPt: "",
    price: "",
    unitEn: "",
    unitPt: "",
    order: ""
  });

  const fetchPackagingMaterials = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get("/packaging-materials");
      if (response.data.error === false && response.data.data) {
        setMaterials(response.data.data);
        // Initialize editing states
        const initialEditingStates = {};
        response.data.data.forEach(material => {
          initialEditingStates[material._id] = {
            nameEn: material.nameEn || "",
            namePt: material.namePt || "",
            descriptionEn: material.descriptionEn || "",
            descriptionPt: material.descriptionPt || "",
            price: material.price || 0,
            unitEn: material.unitEn || "",
            unitPt: material.unitPt || "",
            order: material.order || 0
          };
        });
        setEditingStates(initialEditingStates);
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || "Failed to fetch packaging materials", {
        variant: "error"
      });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchPackagingMaterials();
  }, [fetchPackagingMaterials]);

  const handleFieldChange = (materialId, field, value) => {
    setEditingStates(prev => ({
      ...prev,
      [materialId]: {
        ...prev[materialId],
        [field]: value
      }
    }));
  };

  const handleSave = async materialId => {
    try {
      setSavingStates(prev => ({ ...prev, [materialId]: true }));
      const updatedData = editingStates[materialId];
      console.log("updatedData", updatedData);

      // Only send fields that are allowed to be updated
      const payload = {
        nameEn: updatedData.nameEn,
        namePt: updatedData.namePt,
        descriptionEn: updatedData.descriptionEn,
        descriptionPt: updatedData.descriptionPt,
        price: Number(updatedData.price),
        unitEn: updatedData.unitEn,
        unitPt: updatedData.unitPt,
        order: Number(updatedData.order),
        hasTooltip: updatedData.descriptionEn || updatedData.descriptionPt ? true : false
      };

      const response = await axios.patch(`/admin/packaging-materials/${materialId}`, payload);

      if (response.data.error === false) {
        // Update the material in the list
        setMaterials(prev => prev.map(material => (material._id === materialId ? { ...material, ...updatedData } : material)));
        enqueueSnackbar(t("packagingMaterials.updateSuccess", "Packaging material updated successfully"), {
          variant: "success"
        });
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || t("packagingMaterials.updateError", "Failed to update packaging material"), { variant: "error" });
    } finally {
      setSavingStates(prev => ({ ...prev, [materialId]: false }));
    }
  };

  const handleCreateMaterial = async () => {
    // Validate required fields
    if (!newMaterial.nameEn || !newMaterial.namePt || !newMaterial.descriptionEn || !newMaterial.descriptionPt || !newMaterial.price || !newMaterial.unitEn || !newMaterial.unitPt || !newMaterial.order) {
      enqueueSnackbar(t("validation.required", "All fields are required"), { variant: "error" });
      return;
    }

    try {
      const payload = {
        nameEn: newMaterial.nameEn,
        namePt: newMaterial.namePt,
        descriptionEn: newMaterial.descriptionEn,
        descriptionPt: newMaterial.descriptionPt,
        price: Number(newMaterial.price),
        unitEn: newMaterial.unitEn,
        unitPt: newMaterial.unitPt,
        order: Number(newMaterial.order),
        hasTooltip: newMaterial.descriptionEn || newMaterial.descriptionPt ? true : false
      };

      const response = await axios.post("/admin/packaging-materials", payload);

      if (response.data.error === false) {
        enqueueSnackbar(t("packagingMaterials.createSuccess", "Packaging material created successfully"), { variant: "success" });
        setOpenCreateDialog(false);
        setNewMaterial({
          nameEn: "",
          namePt: "",
          descriptionEn: "",
          descriptionPt: "",
          price: "",
          unitEn: "",
          unitPt: "",
          order: ""
        });
        fetchPackagingMaterials();
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || t("packagingMaterials.createError", "Failed to create packaging material"), { variant: "error" });
    }
  };

  const handleDeleteMaterial = async materialId => {
    if (!window.confirm(t("packagingMaterials.deleteConfirm", "Are you sure you want to delete this packaging material?"))) {
      return;
    }

    try {
      setDeletingMaterials(prev => ({ ...prev, [materialId]: true }));
      const response = await axios.delete(`/admin/packaging-materials/${materialId}`);

      if (response.data.error === false) {
        enqueueSnackbar(t("packagingMaterials.deleteSuccess", "Packaging material deleted successfully"), { variant: "success" });
        fetchPackagingMaterials();
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || t("packagingMaterials.deleteError", "Failed to delete packaging material"), { variant: "error" });
    } finally {
      setDeletingMaterials(prev => ({ ...prev, [materialId]: false }));
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={classes.container}>
      <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpenCreateDialog(true)} className={classes.addButton}>
        {t("packagingMaterials.addNew", "Add New Packaging Material")}
      </Button>
      <Paper className={classes.table}>
        <Box p={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t("packagingMaterials.title", "Packaging Materials")}</Typography>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpenCreateDialog(true)}>
              {t("packagingMaterials.addNew", "Add New Material")}
            </Button>
          </Box>
        </Box>
        <Table>
          <TableHead>
            <TableRow className={classes.headerRow}>
              <TableCell className={classes.tableCell}>{t("packagingMaterials.nameEn", "Name (EN)")}</TableCell>
              <TableCell className={classes.tableCell}>{t("packagingMaterials.namePt", "Name (PT)")}</TableCell>
              <TableCell className={classes.tableCell}>{t("packagingMaterials.descriptionEn", "Description (EN)")}</TableCell>
              <TableCell className={classes.tableCell}>{t("packagingMaterials.descriptionPt", "Description (PT)")}</TableCell>
              <TableCell className={classes.tableCell}>{t("packagingMaterials.price", "Price")}</TableCell>
              <TableCell className={classes.tableCell}>{t("packagingMaterials.unitEn", "Unit (EN)")}</TableCell>
              <TableCell className={classes.tableCell}>{t("packagingMaterials.unitPt", "Unit (PT)")}</TableCell>
              <TableCell className={classes.tableCell}>{t("packagingMaterials.order", "Order")}</TableCell>
              <TableCell className={classes.tableCell}>{t("common.actions", "Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {materials.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {t("common.noData", "No data available")}
                </TableCell>
              </TableRow>
            ) : (
              materials
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(material => {
                  const editingState = editingStates[material._id] || {};
                  const isSaving = savingStates[material._id] || false;

                  return (
                    <TableRow key={material._id}>
                      <TableCell className={classes.tableCell}>
                        <TextField
                          className={classes.input}
                          size="small"
                          variant="outlined"
                          value={editingState.nameEn || ""}
                          onChange={e => handleFieldChange(material._id, "nameEn", e.target.value)}
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <TextField
                          className={classes.input}
                          size="small"
                          variant="outlined"
                          value={editingState.namePt || ""}
                          onChange={e => handleFieldChange(material._id, "namePt", e.target.value)}
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <TextField
                          className={classes.input}
                          size="small"
                          variant="outlined"
                          value={editingState.descriptionEn || ""}
                          onChange={e => handleFieldChange(material._id, "descriptionEn", e.target.value)}
                          multiline
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <TextField
                          className={classes.input}
                          size="small"
                          variant="outlined"
                          value={editingState.descriptionPt || ""}
                          onChange={e => handleFieldChange(material._id, "descriptionPt", e.target.value)}
                          multiline
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <TextField
                          className={classes.input}
                          size="small"
                          variant="outlined"
                          type="number"
                          value={editingState.price || ""}
                          onChange={e => handleFieldChange(material._id, "price", e.target.value)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">€</InputAdornment>
                          }}
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <TextField
                          className={classes.input}
                          size="small"
                          variant="outlined"
                          value={editingState.unitEn || ""}
                          onChange={e => handleFieldChange(material._id, "unitEn", e.target.value)}
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <TextField
                          className={classes.input}
                          size="small"
                          variant="outlined"
                          value={editingState.unitPt || ""}
                          onChange={e => handleFieldChange(material._id, "unitPt", e.target.value)}
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <TextField
                          className={classes.input}
                          size="small"
                          variant="outlined"
                          type="number"
                          value={editingState.order || ""}
                          onChange={e => handleFieldChange(material._id, "order", e.target.value)}
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Button variant="contained" color="primary" size="small" onClick={() => handleSave(material._id)} disabled={isSaving} className={classes.saveButton}>
                            {isSaving ? t("common.loading", "Loading...") : t("common.save", "Save")}
                          </Button>
                          <IconButton color="secondary" size="small" onClick={() => handleDeleteMaterial(material._id)} disabled={deletingMaterials[material._id] || false}>
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create Material Dialog */}
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("packagingMaterials.addNew", "Add New Packaging Material")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" mt={1}>
            <TextField
              label={t("packagingMaterials.nameEn") + " *"}
              value={newMaterial.nameEn}
              onChange={e => setNewMaterial({ ...newMaterial, nameEn: e.target.value })}
              variant="outlined"
              fullWidth
              style={{ marginBottom: "16px" }}
            />
            <TextField
              label={t("packagingMaterials.namePt") + " *"}
              value={newMaterial.namePt}
              onChange={e => setNewMaterial({ ...newMaterial, namePt: e.target.value })}
              variant="outlined"
              fullWidth
              style={{ marginBottom: "16px" }}
            />
            <TextField
              label={t("packagingMaterials.descriptionEn") + " *"}
              value={newMaterial.descriptionEn}
              onChange={e => setNewMaterial({ ...newMaterial, descriptionEn: e.target.value })}
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              style={{ marginBottom: "16px" }}
            />
            <TextField
              label={t("packagingMaterials.descriptionPt") + " *"}
              value={newMaterial.descriptionPt}
              onChange={e => setNewMaterial({ ...newMaterial, descriptionPt: e.target.value })}
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              style={{ marginBottom: "16px" }}
            />
            <TextField
              label={t("packagingMaterials.price") + " *"}
              value={newMaterial.price}
              onChange={e => setNewMaterial({ ...newMaterial, price: e.target.value })}
              variant="outlined"
              type="number"
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">€</InputAdornment>
              }}
              style={{ marginBottom: "16px" }}
            />
            <TextField
              label={t("packagingMaterials.unitEn") + " *"}
              value={newMaterial.unitEn}
              onChange={e => setNewMaterial({ ...newMaterial, unitEn: e.target.value })}
              variant="outlined"
              fullWidth
              style={{ marginBottom: "16px" }}
            />
            <TextField
              label={t("packagingMaterials.unitPt") + " *"}
              value={newMaterial.unitPt}
              onChange={e => setNewMaterial({ ...newMaterial, unitPt: e.target.value })}
              variant="outlined"
              fullWidth
              style={{ marginBottom: "16px" }}
            />
            <TextField
              label={t("packagingMaterials.order") + " *"}
              value={newMaterial.order}
              onChange={e => setNewMaterial({ ...newMaterial, order: e.target.value })}
              variant="outlined"
              type="number"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)} color="default">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleCreateMaterial} color="primary" variant="contained">
            {t("common.add")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PackagingMaterials;
